import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, ChevronRight, Users, Tag } from "lucide-react";

const STATUS = ["pianificato", "confermato", "in_corso", "completato", "annullato"];
const STATUS_TONE = { pianificato: "secondary", confermato: "default", in_corso: "default",
                      completato: "secondary", annullato: "outline" };

export default function Corsi() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [instructorOpen, setInstructorOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  const load = () => api.get("/courses").then(({ data }) => setRows(data));
  const loadInstructors = () => api.get("/instructors").then(({ data }) => setInstructors(data));
  const loadCategories = () => api.get("/categories", { params: { kind: "corso" } }).then(({ data }) => setCategories(data));

  useEffect(() => { load(); loadInstructors(); loadCategories(); }, []);

  const fields = [
    { name: "title", label: "Titolo corso", required: true, full: true },
    {
      name: "category", label: "Categoria", type: "select",
      options: categories.map((c) => ({ value: c.name, label: c.name })),
    },
    {
      name: "instructor_id", label: "Docente", type: "select",
      options: instructors.map((i) => ({ value: i.id, label: i.name })),
    },
    { name: "start_at", label: "Inizio", type: "datetime-local", required: true },
    { name: "end_at", label: "Fine", type: "datetime-local" },
    { name: "location", label: "Sede" },
    { name: "max_participants", label: "Posti max", type: "number" },
    { name: "price", label: "Quota di iscrizione €", type: "number" },
    { name: "status", label: "Stato", type: "select", options: STATUS.map((s) => ({ value: s, label: s })) },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const instructorFields = [
    { name: "name", label: "Nome docente", required: true, full: true },
    { name: "email", label: "Email", type: "email" },
    { name: "phone", label: "Telefono" },
    { name: "specialization", label: "Specializzazione", full: true },
  ];

  const categoryFields = [
    { name: "name", label: "Nome categoria", required: true, full: true },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/courses/${editing.id}`, values);
      else await api.post("/courses", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const saveInstructor = async (values) => {
    try {
      await api.post("/instructors", values);
      await loadInstructors();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const saveCategory = async (values) => {
    try {
      await api.post("/categories", { ...values, kind: "corso" });
      await loadCategories();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="corsi-page">
      <PageHeader title="Formazione" subtitle={`${rows.length} corsi`}>
        {user?.role === "azienda" && (
          <>
            <Button variant="outline" className="h-11 rounded-sm" onClick={() => setCategoryOpen(true)}
                    data-testid="new-category-btn">
              <Tag className="h-4 w-4 mr-2" /> Nuova categoria
            </Button>
            <Button variant="outline" className="h-11 rounded-sm" onClick={() => setInstructorOpen(true)}
                    data-testid="new-instructor-btn">
              <Users className="h-4 w-4 mr-2" /> Nuovo docente
            </Button>
            <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-course-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuovo corso
            </Button>
          </>
        )}
      </PageHeader>

      <ListCard testid="corsi-list">
        {rows.map((c) => (
          <div key={c.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{c.code}</span>
                <p className="font-semibold">{c.title}</p>
                <Badge className="rounded-sm capitalize" variant={STATUS_TONE[c.status] || "secondary"}>
                  {c.status.replace("_", " ")}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(c.start_at).toLocaleString("it-IT")} · {c.location || "—"} ·{" "}
                {c.instructor_name || "docente non assegnato"} · {c.enrolled_count}/{c.max_participants} iscritti
                {user?.role === "azienda" && <> · margine {eur(c.budget?.margin)}</>}
              </p>
            </div>
            <div className="flex gap-2">
              {user?.role === "azienda" && (
                <Button variant="outline" size="sm" className="rounded-sm"
                        onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-course-${c.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button asChild size="sm" className="rounded-sm">
                <Link to={`/formazione/${c.id}`} data-testid={`open-course-${c.id}`}>
                  Apri <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun corso programmato." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica corso" : "Nuovo corso"} fields={fields}
                  initial={editing || { status: "pianificato", max_participants: 20 }} onSubmit={save} testid="course-dialog" />
      <FormDialog open={instructorOpen} onOpenChange={setInstructorOpen} title="Nuovo docente"
                  fields={instructorFields} initial={{}} onSubmit={saveInstructor} testid="instructor-dialog" />
      <FormDialog open={categoryOpen} onOpenChange={setCategoryOpen} title="Nuova categoria"
                  fields={categoryFields} initial={{}} onSubmit={saveCategory} testid="category-dialog" />
    </div>
  );
}
